import merge from "lodash/merge";
import { ChartCandle, HistogramData, HistogramSeriesOptions } from "../chart.typings";
import BaseChartSeries from "./baseSeries";
import { histogramSeriesOptions } from "../../../assets/constants";
import { DeepPartial } from "lightweight-charts";
import tinycolor from "tinycolor2";
import { evaluateExpression } from "../../../util/safeExpressionEval";

class HistogramSeries extends BaseChartSeries<HistogramData, HistogramSeriesOptions> {

    addSeriesToChart() {
        return this.chart.chartApi.addHistogramSeries(this.seriesOptions)
    }

    getDefaultOptions(): HistogramSeriesOptions {
        const {thickness} = this.config;
        const options: HistogramSeriesOptions = {
            source: "close",
            columnWidth: thickness || 1
        };
        return merge({}, histogramSeriesOptions, options, this.options)
    }
    onOptionsUpdate(options: DeepPartial<HistogramSeriesOptions>): void {
            this._setData(this.rawData, this.metadata)
    }

    generateSeriesData(data: ChartCandle[], metadata: Record<string, any> = {}): HistogramData[] {
        const result: HistogramData[] = [];
        const open: number[] = new Array(data.length);
        const close: number[] = new Array(data.length);
        const series: number[] = new Array(data.length);
        const referenceSeries: number[] = new Array(data.length);

        const { color: defaultSeriesColor } = this.seriesApi.options();
        const { color: colorExpr, label: labelExpr } = metadata;
        const colorObj = tinycolor(colorExpr);
        const isColorValid = colorExpr && colorObj.isValid();
        const source = this.seriesOptions.source;

        // If color is valid and differs from series default, apply it once
        if (isColorValid && defaultSeriesColor !== this.options.color) {
            this.seriesApi.applyOptions({ color: colorExpr });
        }

        let valuePresent = false;

        data.forEach((candle, i) => {
            open[i] = candle.open;
            close[i] = candle.close;
            series[i] = candle.series || 0;
            referenceSeries[i] = candle.referenceSeries || 0;

            const val = candle[source];
            if (!val) {
                if (!valuePresent) return;
                result.push({
                    ...candle,
                    value: Number.NaN,
                    color: "transparent",
                    label: ""
                });
                return;
            }

            valuePresent = true;

            const upColor = this.seriesOptions.upColor || "#26a69a80";
            const downColor = this.seriesOptions.downColor || "#ef535080";

            let candleColor: string;
            if (isColorValid) {
                candleColor = colorExpr;
            } else if (colorExpr) {
                try {
                    candleColor = evaluateExpression<string>(colorExpr, { open, close, series, referenceSeries, candle, i });
                } catch (e) {
                    console.warn("Invalid color expression:", colorExpr);
                    candleColor = candle.close > candle.open ? upColor : downColor;
                }
            } else {
                candleColor = candle.close > candle.open ? upColor : downColor;
            }

            result.push({
                ...candle,
                value: val as number,
                color: candleColor,
                label: labelExpr ? String(evaluateExpression(labelExpr, { open, close, series, referenceSeries, candle, i })) : ''
            });
        });

        return result;
    }


}

export default HistogramSeries;